import type { ReactNode } from "react";

/* ------------------------------------------------------------------ */
/* PageSection — standard white content card wrapper                   */
/* ------------------------------------------------------------------ */

export function PageSection({
  title,
  right,
  children,
  bodyClassName = "",
}: {
  title?: string;
  right?: ReactNode;
  children: ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="bg-surface-white border border-default rounded-xl overflow-hidden">
      {(title || right) && (
        <div className="flex items-center justify-between px-[30px] pt-[22px] pb-[18px] border-b border-default">
          {title && <h2 className="text-heading font-semibold text-primary">{title}</h2>}
          {right}
        </div>
      )}
      <div className={bodyClassName || "px-[30px] py-[24px]"}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* ReportTable — header row (surface-light) + divided data rows        */
/* ------------------------------------------------------------------ */

export interface Column {
  label: string;
  align?: "left" | "right" | "center";
  width?: string;
}

export function ReportTable({
  columns,
  rows,
  rowClassName,
}: {
  columns: Column[];
  rows: ReactNode[][];
  rowClassName?: (rowIndex: number) => string;
}) {
  const alignClass = (a?: string) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  return (
    <div className="overflow-x-auto scroll-thin">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-surface-light">
            {columns.map((c, i) => (
              <th
                key={i}
                style={c.width ? { width: c.width } : undefined}
                className={`px-[15px] py-[13px] text-caption font-medium text-secondary ${alignClass(
                  c.align,
                )} ${i === 0 ? "rounded-l-xs" : ""} ${
                  i === columns.length - 1 ? "rounded-r-xs" : ""
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className={`border-b border-default hover:bg-surface-light/60 transition-colors ${rowClassName?.(ri) ?? ""}`}
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={`px-[15px] py-[16px] text-body text-secondary align-middle ${alignClass(
                    columns[ci]?.align,
                  )}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pagination — "1-N from M" with chevrons                             */
/* ------------------------------------------------------------------ */

export function Pagination({ from, to, total }: { from: number; to: number; total: number }) {
  return (
    <div className="flex items-center justify-end gap-3 pt-4">
      <button className="w-8 h-8 rounded-xs border border-default bg-surface-white text-tertiary flex items-center justify-center hover:bg-surface-light transition-colors">
        <i className="fa-solid fa-chevron-left text-[11px]" />
      </button>
      <span className="text-caption text-tertiary">
        {from}-{to} from {total}
      </span>
      <button className="w-8 h-8 rounded-xs border border-default bg-surface-white text-tertiary flex items-center justify-center hover:bg-surface-light transition-colors">
        <i className="fa-solid fa-chevron-right text-[11px]" />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* StatusBadge — green / amber / red / neutral pill                    */
/* ------------------------------------------------------------------ */

export function StatusBadge({
  label,
  tone = "neutral",
  ariaLabel,
}: {
  label: string;
  tone?: "green" | "amber" | "red" | "neutral" | "blue";
  ariaLabel?: string;
}) {
  const styles: Record<string, string> = {
    green: "text-status-green bg-[rgba(86,205,174,0.12)]",
    amber: "text-[#a06c00] bg-[rgba(255,184,53,0.16)]",
    red: "text-status-red bg-[rgba(255,101,80,0.12)]",
    blue: "text-status-blue bg-[rgba(38,164,255,0.12)]",
    neutral: "text-secondary bg-surface-light border border-default",
  };
  return (
    <span
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1.5 text-micro font-semibold px-2 py-1 rounded-xs ${styles[tone]}`}
    >
      {label}
    </span>
  );
}
